/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2022.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {Box, Grid} from "@mui/material";
import {geoAlbersUsa, geoPath} from "d3-geo";
import React, {useMemo} from "react";
import {feature} from "topojson-client";

////////////////////////////////////////////////////////////////////////////////
// us-atlas ships this topology pre-projected (composite Albers, with Alaska  //
// and Hawaii insets) to a 975x610 viewport - so states render with a null    //
// projection, and markers use the equivalent geoAlbersUsa projection below.  //
////////////////////////////////////////////////////////////////////////////////
// eslint-disable-next-line @typescript-eslint/no-var-requires
const statesTopology: any = require("us-atlas/states-albers-10m.json");

const VIEWBOX_WIDTH = 975;
const VIEWBOX_HEIGHT = 610;
const markerProjection = geoAlbersUsa().scale(1300).translate([VIEWBOX_WIDTH / 2, VIEWBOX_HEIGHT / 2]);
const statesPathGenerator = geoPath();


////////////////////////////////////////////////
// structure of expected US and A widget data //
////////////////////////////////////////////////
export interface MapMarkerData
{
   name: string;
   latitude: number;
   longitude: number;
}
export interface USMapWidgetData
{
   height: string;
   mapMarkerList?: MapMarkerData[];
   markers?: MapMarkerData[];
}


////////////////////////////////////
// define properties and defaults //
////////////////////////////////////
interface Props
{
   widgetIndex: number;
   label: string;
   icon?: string;
   reloadWidgetCallback?: (widgetIndex: number, params: string) => void;
   data: USMapWidgetData;
}


function USMapWidget(props: Props): JSX.Element
{
   const statesPath = useMemo(() =>
      statesPathGenerator(feature(statesTopology, statesTopology.objects.states)), []);

   const markers: MapMarkerData[] = props.data?.mapMarkerList ?? props.data?.markers ?? [];

   return (
      <Grid container>
         <Grid item xs={12} sx={{height: props.data?.height}}>
            {
               props.data?.height && (
                  <Box mt={3} sx={{height: "100%", display: "flex", justifyContent: "center"}}>
                     <svg viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`} style={{height: "100%", maxWidth: "100%", marginTop: "-1.5rem"}}>
                        <path d={statesPath} fill="#dee2e7" stroke="#ffffff" strokeWidth={1} />
                        {
                           markers.map((marker, index) =>
                           {
                              const point = markerProjection([marker.longitude, marker.latitude]);
                              return point && (
                                 <circle
                                    key={`${marker.name}-${index}`}
                                    cx={point[0]}
                                    cy={point[1]}
                                    r={17}
                                    fill="#e91e63"
                                    stroke="#ffffff"
                                    strokeWidth={10}
                                    strokeOpacity={0.5}
                                 />
                              );
                           })
                        }
                     </svg>
                  </Box>
               )
            }

         </Grid>
      </Grid>
   );
}

export default USMapWidget;
